import {
    Button,
    Drawer,
    DrawerProps,
    Group,
    ScrollArea,
    Select,
    Stack,
} from '@mantine/core';
import {Formik} from 'formik';
import {I18n} from '@aws-amplify/core';

import {
    SetupIntentsFilters,
    useSetupIntentsFilter,
} from '../hooks/useSetupIntentsFilter';

/**
 * FNP-918: the setup-intents filter drawer (§7).
 *
 * Formik-driven, matching the form conventions in §6. Filter state lives in the
 * MODULE-SCOPED store (useSetupIntentsFilter), not component state, so the
 * table — a sibling, not a child — filters the same list.
 *
 * ⚠️ BOTH FILTERS BELOW ARE CLIENT-SIDE, unlike payment-methods' `type`.
 * `SetupIntentsFilterInput` declares `{customerId: String}` and nothing else,
 * so the API cannot narrow by status or usage. They are matched over the pages
 * loaded so far and are NEVER sent as query variables — AppSync silently
 * ignores an undeclared input field, so sending them would return the
 * unfiltered list while this drawer showed an active badge.
 *
 * The values come straight from the schema enums (SetupIntentStatus,
 * SetupIntentUsage), so a select can never offer a value the data cannot hold.
 */
export const FilterDrawer = ({
                                 opened,
                                 onClose,
                                 ...rest
                             }: DrawerProps) => {

    const {
        filters,
        handleSetFilters,
        handleClearFilters,
    } = useSetupIntentsFilter();

    return (
        <Drawer
            transitionProps={{
                transition: 'slide-left',
                duration: 750,
                timingFunction: 'ease',
            }}
            position='right'
            offset={10}
            opened={opened}
            onClose={onClose}
            title={I18n.get('Filter Setup Intents')}
            padding='xl'
            size='md'
            scrollAreaComponent={ScrollArea.Autosize}
            {...rest}
        >
            <Formik<SetupIntentsFilters>
                initialValues={filters}
                enableReinitialize
                onSubmit={(values) => {
                    handleSetFilters(values);
                    onClose();
                }}
            >
                {({
                      values,
                      setFieldValue,
                      handleSubmit,
                      resetForm,
                  }) => (
                    <form onSubmit={(event) => {
                        event.preventDefault();
                        handleSubmit();
                    }}>
                        <Stack gap='md'>
                            <Select
                                label={I18n.get('Status')}
                                placeholder={I18n.get('Any')}
                                clearable
                                data={[
                                    {
                                        value: 'succeeded',
                                        label: I18n.get('Succeeded'),
                                    },
                                    {
                                        value: 'processing',
                                        label: I18n.get('Processing'),
                                    },
                                    {
                                        value: 'requiresAction',
                                        label: I18n.get('Requires action'),
                                    },
                                    {
                                        value: 'requiresConfirmation',
                                        label: I18n.get('Requires confirmation'),
                                    },
                                    {
                                        value: 'requiresPaymentMethod',
                                        label: I18n.get('Requires payment method'),
                                    },
                                    {
                                        value: 'canceled',
                                        label: I18n.get('Canceled'),
                                    },
                                ]}
                                value={values.status ?? null}
                                onChange={(value) =>
                                    setFieldValue('status', value ?? '')}
                            />

                            <Select
                                label={I18n.get('Usage')}
                                placeholder={I18n.get('Any')}
                                clearable
                                data={[
                                    {
                                        value: 'offSession',
                                        label: I18n.get('Off session'),
                                    },
                                    {
                                        value: 'onSession',
                                        label: I18n.get('On session'),
                                    },
                                ]}
                                value={values.usage ?? null}
                                onChange={(value) =>
                                    setFieldValue('usage', value ?? '')}
                            />

                            <Group justify='flex-end' mt='md'>
                                {/*
                                  * Clears EVERY field and is reachable from the
                                  * drawer (§7) — it resets both the store and
                                  * the visible form, so the two cannot disagree.
                                  */}
                                <Button
                                    variant='subtle'
                                    onClick={() => {
                                        handleClearFilters();
                                        resetForm({values: {}});
                                    }}
                                >
                                    {I18n.get('Clear filters')}
                                </Button>
                                <Button type='submit'>
                                    {I18n.get('Apply')}
                                </Button>
                            </Group>
                        </Stack>
                    </form>
                )}
            </Formik>
        </Drawer>
    );
};
